import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Switch, FormControlLabel,
    TextField, MenuItem, Select, FormControl, InputLabel, RadioGroup,
    Radio, Divider, Button, Snackbar, Alert, Chip, Stack,
    CircularProgress,
} from '@mui/material';
import {
    Settings as SettingsIcon,
    AccountCircle as ApproverIcon,
    Schedule as HoursIcon,
    Save as SaveIcon,
    VerifiedUser as TwoLevelIcon,
} from '@mui/icons-material';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

// ─── Section Wrapper ──────────────────────────────────────────────────────────
function ConfigSection({ title, subtitle, icon: Icon, iconColor = '#6366f1', children }) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 3, mb: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <Box sx={{ bgcolor: `${iconColor}1a`, borderRadius: 2, p: 1.25, mt: 0.25 }}>
                        <Icon sx={{ fontSize: 22, color: iconColor }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{title}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>{subtitle}</Typography>
                    </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />
                {children}
            </CardContent>
        </Card>
    );
}

// ─── Field Row ────────────────────────────────────────────────────────────────
function FieldRow({ label, hint, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ width: 240, flexShrink: 0, pt: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{label}</Typography>
                {hint && (
                    <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.25 }}>{hint}</Typography>
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
                {children}
            </Box>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TimesheetWorkflowConfig({ refreshKey }) {
    const organizationId = JSON.parse(localStorage.getItem('userData') || '{}')?.organization_id;

    // ── Approval Workflow State ──────────────────────────────────────────────
    const [twoLevelEnabled, setTwoLevelEnabled] = useState(false);
    const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false);
    const [externalApproverId, setExternalApproverId] = useState('');
    const [managerCanEdit, setManagerCanEdit] = useState(true);
    const [externalCanEdit, setExternalCanEdit] = useState(false);
    const [applyLevel, setApplyLevel] = useState('company');   // 'company' | 'project'

    // ── Working Hours Policy State ───────────────────────────────────────────
    const [maxDailyHours, setMaxDailyHours] = useState(8);
    const [minWeeklyHours, setMinWeeklyHours] = useState(0);
    const [maxWeeklyHours, setMaxWeeklyHours] = useState(168);

    // ── Notification Policy State ────────────────────────────────────────────
    const [reminderEnabled, setReminderEnabled] = useState(true);
    const [reminderDays, setReminderDays] = useState(2);

    // ── Data ─────────────────────────────────────────────────────────────────
    const [externalApprovers, setExternalApprovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

    // ── Load config + external approvers on mount ────────────────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const [configRes, approversRes] = await Promise.all([
                    getData(API_ENDPOINTS.getTimesheetConfig()),
                    organizationId
                        ? getData(API_ENDPOINTS.getExternalApprovers(organizationId))
                        : Promise.resolve(null),
                ]);

                const cfg = configRes?.result?.data ?? configRes?.result ?? null;
                if (cfg) {
                    setTwoLevelEnabled(cfg.is_two_level_approval ?? false);
                    // Auto-approval only has meaning when two-level approval is on —
                    // ignore a stale value saved from an earlier config state.
                    setAutoApprovalEnabled(!!cfg.is_two_level_approval && (cfg.enable_manager_auto_approval ?? false));
                    setExternalApproverId(cfg.external_approver_id ?? '');
                    setManagerCanEdit(cfg.allow_manager_edit ?? true);
                    setExternalCanEdit(cfg.allow_external_edit ?? false);
                    // approval_scope only has meaning when two-level approval is on —
                    // ignore a stale PROJECT scope saved from an earlier config state.
                    setApplyLevel(cfg.is_two_level_approval && cfg.approval_scope === 'PROJECT' ? 'project' : 'company');
                    setMaxDailyHours(cfg.max_daily_hours ?? 8);
                    setMinWeeklyHours(cfg.min_weekly_hours ?? 0);
                    setMaxWeeklyHours(cfg.max_weekly_hours ?? 168);
                    setReminderDays(cfg.reminder_frequency_days ?? 2);
                }

                const approvers = approversRes?.message?.users ?? approversRes?.result?.data ?? approversRes?.result ?? [];
                setExternalApprovers(Array.isArray(approvers) ? approvers : []);
            } catch (err) {
                console.error('Failed to load timesheet config:', err);
                setSnackbar({ open: true, msg: 'Failed to load configuration', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [organizationId, refreshKey]);

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        try {
            await postData(API_ENDPOINTS.saveTimesheetConfig(), {
                is_two_level_approval: twoLevelEnabled,
                enable_manager_auto_approval: autoApprovalEnabled,
                external_approver_id: applyLevel === 'company' ? (externalApproverId || null) : null,
                allow_manager_edit: managerCanEdit,
                allow_external_edit: twoLevelEnabled && externalCanEdit,
                approval_scope: (twoLevelEnabled && applyLevel === 'project') ? 'PROJECT' : 'COMPANY',
                max_daily_hours: maxDailyHours,
                min_weekly_hours: minWeeklyHours,
                max_weekly_hours: maxWeeklyHours,
                reminder_frequency_days: reminderDays,
            });
            setSnackbar({ open: true, msg: 'Configuration saved successfully!', severity: 'success' });
        } catch (err) {
            console.error('Failed to save config:', err);
            setSnackbar({ open: true, msg: 'Failed to save configuration', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const selectedApprover = externalApprovers.find(a => a.id === externalApproverId || a.id === Number(externalApproverId));

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* ── Section A: Approval Workflow ──────────────────────────── */}
            <ConfigSection
                title="Approval Workflow"
                subtitle="Configure single or two-level approval, assign external approvers, and set edit permissions"
                icon={TwoLevelIcon}
                iconColor="#6366f1"
            >
                {/* Two-Level Toggle */}
                <FieldRow
                    label="Enable Two-Level Approval"
                    hint="When enabled, timesheets require both Manager and External Approver sign-off"
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={twoLevelEnabled}
                                    onChange={e => {
                                        setTwoLevelEnabled(e.target.checked);
                                        if (!e.target.checked) {
                                            setApplyLevel('company');
                                            setAutoApprovalEnabled(false);
                                            setExternalCanEdit(false);
                                        }
                                    }}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: twoLevelEnabled ? 700 : 400 }}>
                                    {twoLevelEnabled ? 'Two-Level Approval Active' : 'Single-Level Approval'}
                                </Typography>
                            }
                        />
                        <Chip
                            label={twoLevelEnabled ? 'Employee → External Approver → Manager ' : 'Employee → Manager'}
                            size="small"
                            sx={{
                                bgcolor: twoLevelEnabled ? '#eef2ff' : '#f1f5f9',
                                color: twoLevelEnabled ? '#6366f1' : '#64748b',
                                fontWeight: 600, fontSize: 11,
                            }}
                        />
                    </Box>
                </FieldRow>

                {/* Approval Scope — company vs project (only relevant when two-level is enabled) */}
                {twoLevelEnabled && (
                    <FieldRow
                        label="Approval Flow Scope"
                        hint="Company: one external approver for all timesheets. Project: each project sets its own external approver."
                    >
                        <RadioGroup row value={applyLevel} onChange={e => setApplyLevel(e.target.value)}>
                            <FormControlLabel
                                value="company"
                                control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 13 }}>Company Level</Typography>}
                            />
                            <FormControlLabel
                                value="project"
                                control={<Radio size="small" />}
                                label={<Typography sx={{ fontSize: 13 }}>Project Level</Typography>}
                            />
                        </RadioGroup>
                        {applyLevel === 'project' && (
                            <Typography sx={{ fontSize: 12, color: '#6366f1', mt: 0.5 }}>
                                External approvers are configured per-project in Project Settings. Entry-level external approval is used.
                            </Typography>
                        )}
                    </FieldRow>
                )}

                {/* External Approver — only for COMPANY scope + two-level enabled */}
                {twoLevelEnabled && applyLevel === 'company' && (
                    <FieldRow label="Assign External Approver" hint="Company-wide external approver for all timesheets">
                        <FormControl size="small" fullWidth>
                            <InputLabel>Select External Approver</InputLabel>
                            <Select
                                value={externalApproverId}
                                label="Select External Approver"
                                onChange={e => setExternalApproverId(e.target.value)}
                                startAdornment={<ApproverIcon sx={{ fontSize: 18, mr: 0.5, color: '#94a3b8' }} />}
                            >
                                {externalApprovers.map(a => (
                                    <MenuItem key={a.id} value={a.id}>
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                                {a.first_name} {a.last_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{a.email}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {selectedApprover && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#eef2ff', borderRadius: 2, border: '1px solid #c7d2fe' }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>
                                    {selectedApprover.first_name} {selectedApprover.last_name}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{selectedApprover.email}</Typography>
                            </Box>
                        )}
                    </FieldRow>
                )}

                {/* Edit Permissions — hidden only for two-level + Project scope (that scope manages edit access per-project instead) */}
                {(!twoLevelEnabled || applyLevel === 'company') && (
                    <FieldRow label="Edit Permissions" hint="Control who can edit timesheet entries after submission">
                        <Stack spacing={1}>
                            <FormControlLabel
                                control={<Switch size="small" checked={managerCanEdit} onChange={e => setManagerCanEdit(e.target.checked)} />}
                                label={<Typography sx={{ fontSize: 13 }}>Allow Manager to edit entries</Typography>}
                            />
                            {twoLevelEnabled && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={externalCanEdit}
                                            onChange={e => setExternalCanEdit(e.target.checked)}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: 13 }}>Allow External Approver to edit entries</Typography>}
                                />
                            )}
                        </Stack>
                    </FieldRow>
                )}

                {/* Manager Auto-Approval — only meaningful under two-level approval */}
                {twoLevelEnabled && (
                    applyLevel === 'company' ? (
                        <FieldRow
                            label="Internal Manager Auto-Approval"
                            hint="When enabled, timesheets are automatically approved by the internal manager upon submission"
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoApprovalEnabled}
                                        onChange={e => setAutoApprovalEnabled(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: autoApprovalEnabled ? 700 : 400 }}>
                                        {autoApprovalEnabled ? 'Auto-Approval Active' : 'Manual Approval Required'}
                                    </Typography>
                                }
                            />
                        </FieldRow>
                    ) : (
                        <FieldRow
                            label="Internal Manager Auto-Approval"
                            hint="Configured per-project in Project Settings when scope is Project"
                        >
                            <Typography sx={{ fontSize: 12, color: '#6366f1' }}>
                                Auto-approval is set per-project in Project Settings. A timesheet only skips its manager if every project it touches has auto-approval enabled.
                            </Typography>
                        </FieldRow>
                    )
                )}
            </ConfigSection>

            {/* ── Section B: Working Hours Policy ───────────────────────── */}
            <ConfigSection
                title="Working Hours Policy"
                subtitle="Set daily and weekly hour limits and rounding rules"
                icon={HoursIcon}
                iconColor="#10b981"
            >
                <FieldRow label="Max Hours Per Day" hint="Maximum regular hours an employee can log in a single day (overtime is uncapped)">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                            type="number"
                            size="small"
                            value={maxDailyHours}
                            onChange={e => setMaxDailyHours(Math.min(24, Math.max(1, Number(e.target.value))))}
                            inputProps={{ min: 1, max: 24 }}
                            sx={{ width: 120 }}
                            InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: '#94a3b8', ml: 0.5 }}>hrs</Typography> }}
                        />
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                            Overtime (OT) hours are not subject to this limit and can be any amount.
                        </Typography>
                    </Box>
                </FieldRow>

                <FieldRow label="Min Hours Per Week" hint="Minimum hours required before submission is valid">
                    <TextField
                        type="number"
                        size="small"
                        value={minWeeklyHours}
                        onChange={e => setMinWeeklyHours(Math.max(0, Number(e.target.value)))}
                        inputProps={{ min: 0 }}
                        sx={{ width: 120 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: '#94a3b8', ml: 0.5 }}>hrs</Typography> }}
                    />
                </FieldRow>

                <FieldRow label="Max Hours Per Week" hint="Maximum total hours allowed per week">
                    <TextField
                        type="number"
                        size="small"
                        value={maxWeeklyHours}
                        onChange={e => setMaxWeeklyHours(Math.min(168, Math.max(1, Number(e.target.value))))}
                        inputProps={{ min: 1, max: 168 }}
                        sx={{ width: 120 }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: '#94a3b8', ml: 0.5 }}>hrs</Typography> }}
                    />
                </FieldRow>


            </ConfigSection>

            {/* ── Section C: Notification Policy ────────────────────────── */}
            <ConfigSection
                title="Notification Settings"
                subtitle="Configure when email and in-app alerts are triggered during the approval workflow"
                icon={SettingsIcon}
                iconColor="#f59e0b"
            >
                <FieldRow label="Pending Approval Reminders" hint="Send reminders to approvers for overdue submissions">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={<Switch size="small" checked={reminderEnabled} onChange={e => setReminderEnabled(e.target.checked)} />}
                            label={<Typography sx={{ fontSize: 13 }}>Enable reminders</Typography>}
                        />
                        {reminderEnabled && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Every</Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={reminderDays}
                                    onChange={e => setReminderDays(Math.max(1, Number(e.target.value)))}
                                    inputProps={{ min: 1, max: 30 }}
                                    sx={{ width: 70 }}
                                />
                                <Typography sx={{ fontSize: 13, color: '#64748b' }}>day(s)</Typography>
                            </Box>
                        )}
                    </Box>
                </FieldRow>
            </ConfigSection>

            {/* ── Save Button ───────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="outlined"
                    sx={{ borderColor: '#e2e8f0', color: '#64748b', fontWeight: 600 }}
                    onClick={() => setSnackbar({ open: true, msg: 'Changes discarded.', severity: 'info' })}
                    disabled={saving}
                >
                    Discard Changes
                </Button>
                <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ bgcolor: '#6366f1', fontWeight: 700, '&:hover': { bgcolor: '#4f46e5' }, px: 3 }}
                >
                    {saving ? 'Saving…' : 'Save Configuration'}
                </Button>
            </Box>

            {/* ── Snackbar ──────────────────────────────────────────────── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
